import { Suspense, createContext, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';

import SignIn from './pages/Authentication/SignIn';
import SignUp from './pages/Authentication/SignUp';
import Loader from './common/Loader';
import routes from './routes';
import RouteAuthGuard from './routes/RouteAuthGuard';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer } from 'react-toastify';
import { clearAllCookies, getCookie } from './helpers/cookieFunctions';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from './helpers/axiosInstance';
import NotFound from './pages/NotFound';
import IdleResumePanel from './components/IdleResumePanel';
import { registerFcmToken } from './helpers/registerFcmToken';

const DefaultLayout = lazy(() => import('./layout/DefaultLayout'));
export const CRMContext = createContext({} as any);

function App() {
  const [loading, setLoading] = useState<boolean>(true);
  // Intro screensaver shown once on every fresh site open (before login or
  // dashboard). The user presses "P" to dismiss it and continue.
  const [introDone, setIntroDone] = useState(false);
  const [token, setToken] = useState(getCookie('peliswan_crm_token') || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [id, setId] = useState('');
  const [rawId, setRawId] = useState('');
  const [filterObj, setFilterObj] = useState({
    unloadingStatus: 'Completed',
    loadingStatus: 'Completed',
    labStatus: true,
    labType: '',
  });
  const [theme, setTheme] = useState(localStorage.getItem('color-theme'));

  const today = new Date();
  const lastMonth = new Date(
    today.getFullYear(),
    today.getMonth() - 1,
    today.getDate(),
  );

  const [paginationObj, setPaginationObj] = useState<{
    [key: string]: {
      [key: string]: number | Date | string | boolean;
    };
  }>({
    vehicleLoading: {
      currentPage: 1,
      recordPerPage: 20,
      directSale: false,
    },
    product: {
      currentPage: 1,
      recordPerPage: 20,
    },
    upcomingUnload: {
      currentPage: 1,
      recordPerPage: 20,
      to: new Date(),
      from: lastMonth,
      search: '',
      searchBy: '',
    },
    directSell: {
      currentPage: 1,
      recordPerPage: 20,
    },
    allSale: {
      currentPage: 1,
      recordPerPage: 20,
      to: new Date(),
      from: lastMonth,
      search: '',
      searchBy: '',
    },
    allPurchase: {
      currentPage: 1,
      recordPerPage: 100,
      to: new Date(),
      from: lastMonth,
      search: '',
      searchBy: '',
    },
    vehicleUnloading: {
      currentPage: 1,
      recordPerPage: 20,
    },
    doAnOrderEntries: {
      currentPage: 1,
      recordPerPage: 20,
      directSale: false,
    },
    samples: {
      currentPage: 1,
      recordPerPage: 20,
    },
  });

  const updatePagination = (
    paginationKey: string,
    newValue: {
      [key: string]: number;
    },
  ) => {
    setPaginationObj((prevPaginationObj) => ({
      ...prevPaginationObj,
      [paginationKey]: {
        ...prevPaginationObj[paginationKey],
        ...newValue,
      },
    }));
  };

  const [products, setProducts] = useState<
    {
      _id: string;
      name: string;
      uniqueId: string;
      price: number;
      density: number;
    }[]
  >([]);

  useEffect(() => {
    try {
      if (!!getCookie('rawId')) {
        const { name } = jwtDecode(getCookie('rawId')) as unknown as {
          name: string;
        };
        setRawId(name);
      }

      if (!!getCookie('peliswan_crm_token')) {
        const { name, _id, email } = jwtDecode(
          getCookie('peliswan_crm_token'),
        ) as unknown as {
          name: string;
          _id: string;
          email: string;
        };
        setEmail(email);
        setName(name);
        setId(_id);
      }
      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      clearAllCookies();
    }
  }, [token]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (getCookie('peliswan_crm_token')) {
          const [productsResult] = await Promise.all([
            axiosInstance.get('/products', {
              params: { fields: 'uniqueId,name,price,density' },
            }),
          ]);

          setProducts(productsResult.data.products);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Handle errors here
      }
    };

    fetchData();
  }, []);

  const findProduct = (productId: string) => {
    return products.find((item) => item._id === productId);
  };

  const [pendingDeviceCount, setPendingDeviceCount] = useState(0);

  useEffect(() => {
    // Only Master Admin needs the global pending device count for approvals
    if (rawId !== 'Master Admin') return;

    const fetchPendingCount = async () => {
      try {
        const res = await axiosInstance.get('/devices/pending/count');
        setPendingDeviceCount(res.data.count);
      } catch (err) {
        console.error(err);
      }
    };

    if (getCookie('peliswan_crm_token')) {
      fetchPendingCount();

      const interval = setInterval(fetchPendingCount, 10000); // every 10 sec
      return () => clearInterval(interval);
    }
  }, [rawId]);

  async function requestPermission() {
    // Register (or refresh) this device's FCM token on app load.
    await registerFcmToken();
  }

  // Defer the native notification-permission prompt until AFTER the intro
  // gate is dismissed.
  useEffect(() => {
    if (!introDone) return;
    requestPermission();
  }, [introDone]);

  return loading ? (
    <Loader />
  ) : !introDone ? (
    <IdleResumePanel onResume={() => setIntroDone(true)} />
  ) : (
    <CRMContext.Provider
      value={{
        theme,
        setTheme,
        token,
        name,
        id,
        email,
        rawId,
        products,
        filterObj,
        paginationObj,
        updatePagination,
        setFilterObj,
        setToken,
        findProduct,
        setName,
        setId,
        setRawId,
        setProducts,
        pendingDeviceCount,
      }}
    >
      <ToastContainer />
      <Routes>
        <Route path="/auth/signin" element={<SignIn />} />
        <Route path="/auth/signup" element={<SignUp />} />
        <Route
          path="/"
          element={
            <Suspense fallback={<Loader />}>
              <DefaultLayout />
            </Suspense>
          }
        >
          {/* <Route index element={<ECommerce />} /> */}

          {routes.map((routes, index) => {
            const { path, component: Component, allowedRoles } = routes;
            return (
              <Route
                key={index}
                path={path}
                element={
                  <RouteAuthGuard
                    component={
                      <Suspense fallback={<Loader />}>
                        <Component />
                      </Suspense>
                    }
                    allowedRoles={allowedRoles}
                    redirect="/auth/signin"
                  />
                }
              />
            );
          })}
        </Route>
        {/* Not Found Page */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </CRMContext.Provider>
  );
}

export default App;
